import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Label, ListBox, Select } from '@heroui/react';
import { DateRangeField } from '@/components/dateRangeField';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ActivityFilterState } from '../../activityMeta';

type ActivityFiltersProps = {
    filters: ActivityFilterState;
    operations: { key: string; label: string }[];
    onRangeChange: (from: string, to: string) => void;
    onKeyChange: (key: string) => void;
    onStatusChange: (status: string) => void;
    /**
     * The register is still in flight. The controls keep their labels and shape
     * and are disabled — the loading-state standard: never replaced by a grey
     * block, never removed.
     */
    isDisabled: boolean;
};

/**
 * The Activity register's filter row: a date range, an operation, a status.
 *
 * **The date range bounds the entry date**, which is also what the register is
 * ordered by and what each row shows. Filtering on when the record was written
 * instead would let a backdated recording fall outside a window that visibly
 * contains the entry it produced.
 *
 * The operation select offers the tenant's **templates only**. A system row —
 * a sale, a payroll run — cannot be recorded here, so offering it would be a
 * choice that always answers empty.
 */
export const ActivityFilters: React.FC<ActivityFiltersProps> = ({
    filters,
    operations,
    onRangeChange,
    onKeyChange,
    onStatusChange,
    isDisabled,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="flex flex-wrap items-end gap-3"
            {...getNodeTestIdProp('operations-activity-filters')}
        >
            <div className="w-full min-w-52 max-w-72">
                <DateRangeField
                    ariaLabel={t('operations.activity.filters.rangeAria')}
                    fromDate={filters.from}
                    isDisabled={isDisabled}
                    onChange={onRangeChange}
                    slot="operations-activity"
                    toDate={filters.to}
                />
            </div>

            <Select
                className="w-56"
                isDisabled={isDisabled}
                onChange={(next) => next && onKeyChange(String(next))}
                value={filters.key}
            >
                <Label className="text-xs text-muted">
                    {t('operations.activity.filters.operation')}
                </Label>
                <Select.Trigger
                    {...getNodeTestIdProp(
                        'operations-activity-filter-operation',
                    )}
                >
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        <ListBox.Item
                            id="ALL"
                            textValue={t(
                                'operations.activity.filters.allOperations',
                            )}
                        >
                            {t('operations.activity.filters.allOperations')}
                            <ListBox.ItemIndicator />
                        </ListBox.Item>
                        {operations.map((operation) => (
                            <ListBox.Item
                                id={operation.key}
                                key={operation.key}
                                textValue={operation.label}
                            >
                                {operation.label}
                                <ListBox.ItemIndicator />
                            </ListBox.Item>
                        ))}
                    </ListBox>
                </Select.Popover>
            </Select>

            <Select
                className="w-44"
                isDisabled={isDisabled}
                onChange={(next) => next && onStatusChange(String(next))}
                value={filters.status}
            >
                <Label className="text-xs text-muted">
                    {t('operations.activity.filters.status')}
                </Label>
                <Select.Trigger
                    {...getNodeTestIdProp('operations-activity-filter-status')}
                >
                    <Select.Value />
                    <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                    <ListBox>
                        {(['ALL', 'POSTED', 'REVERSED'] as const).map(
                            (status) => (
                                <ListBox.Item
                                    id={status}
                                    key={status}
                                    textValue={t(
                                        `operations.activity.filters.statuses.${status}`,
                                    )}
                                >
                                    {t(
                                        `operations.activity.filters.statuses.${status}`,
                                    )}
                                    <ListBox.ItemIndicator />
                                </ListBox.Item>
                            ),
                        )}
                    </ListBox>
                </Select.Popover>
            </Select>
        </div>
    );
};
